import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	CircularProgress,
	FormControl,
	Grid,
	InputLabel,
	Link,
	MenuItem,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from "recharts";
import api from "../services/api";
import { fetchProjects } from "../store/slices/projectSlice";
import { formatDateTime } from "../utils/dateUtils";
import { HistoryDots } from "./Stability";

// Analytics → Quality (GET /api/analytics/quality, QualityService): pass rate, runs and
// duration per day over 7 / 30 / 90 days, optionally for one project, plus the tests that
// failed most, the slowest and the flaky ones. One measure per chart (no second axis);
// days without runs are gaps, not 0%.

const PERIODS = [7, 30, 90];

const fmtDuration = (ms) => {
	if (ms == null) return "–";
	if (ms < 1000) return `${ms} ms`;
	const s = Math.round(ms / 1000);
	return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
};
// Compact, for axis ticks: "800ms", "12s", "3m"
const fmtAxisDuration = (ms) => (ms < 1000 ? `${ms}ms` : ms < 60000 ? `${Math.round(ms / 1000)}s` : `${Math.round(ms / 60000)}m`);
// daily points are UTC days ("2026-09-30")
const fmtDay = (day) => new Date(`${day}T00:00:00Z`).toLocaleDateString(undefined, { day: "numeric", month: "short", timeZone: "UTC" });

function StatTile({ label, value, children }) {
	return (
		<Card sx={{ height: "100%" }}>
			<CardContent>
				<Typography variant="body2" color="text.secondary">
					{label}
				</Typography>
				<Typography variant="h4" fontWeight={700} sx={{ my: 0.5 }}>
					{value}
				</Typography>
				<Box sx={{ minHeight: 20 }}>{children}</Box>
			</CardContent>
		</Card>
	);
}

/** The pass rate's change from the period before, as an arrow + words (not color alone) */
function PassRateChange({ now, before }) {
	if (now == null || before == null) {
		return (
			<Typography variant="caption" color="text.secondary">
				No runs in the period before
			</Typography>
		);
	}
	const diff = Math.round((now - before) * 10) / 10;
	if (diff === 0) {
		return (
			<Typography variant="caption" color="text.secondary">
				Same as the period before
			</Typography>
		);
	}
	const up = diff > 0;
	return (
		<Typography variant="caption" sx={{ display: "inline-flex", alignItems: "center", gap: 0.25, color: up ? "success.main" : "error.main", fontWeight: 600 }}>
			{up ? <ArrowUpward sx={{ fontSize: 14 }} /> : <ArrowDownward sx={{ fontSize: 14 }} />}
			{Math.abs(diff)} points {up ? "up" : "down"} on the period before ({before}%)
		</Typography>
	);
}

function DayTooltip({ active, payload, label }) {
	if (!active || !payload?.length) return null;
	const d = payload[0].payload;
	return (
		<Box sx={{ bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 1, px: 1.5, py: 1, boxShadow: 3 }}>
			<Typography variant="caption" fontWeight={700} component="div">
				{fmtDay(label)}
			</Typography>
			{d.runs === 0 ? (
				<Typography variant="caption" color="text.secondary">
					No runs
				</Typography>
			) : (
				<Typography variant="caption" component="div" color="text.secondary">
					{d.passRate}% passed · {d.runs} runs ({d.passed} passed, {d.failed} failed)
					<br />
					Average {fmtDuration(d.avgDuration)}
				</Typography>
			)}
		</Box>
	);
}

function ChartCard({ title, children }) {
	return (
		<Card sx={{ height: "100%" }}>
			<CardContent>
				<Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
					{title}
				</Typography>
				<Box sx={{ height: 220 }}>{children}</Box>
			</CardContent>
		</Card>
	);
}

function TestLink({ testId, name }) {
	const navigate = useNavigate();
	return (
		<Link component="button" variant="body2" underline="hover" onClick={() => navigate(`/tests/${testId}`)} sx={{ textAlign: "left", fontWeight: 500 }}>
			{name || "Deleted test"}
		</Link>
	);
}

export default function QualityDashboard() {
	const theme = useTheme();
	const dispatch = useDispatch();
	const { projects } = useSelector((state) => state.projects);
	const [days, setDays] = useState(30);
	const [projectId, setProjectId] = useState("");
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [showTable, setShowTable] = useState(false);

	useEffect(() => {
		dispatch(fetchProjects());
	}, [dispatch]);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		setError(null);
		api.get("/analytics/quality", { params: { days, projectId: projectId || undefined } })
			.then((r) => !cancelled && setData(r.data))
			.catch((err) => !cancelled && setError(err.response?.data?.message || "Couldn't load the quality numbers"))
			.finally(() => !cancelled && setLoading(false));
		return () => {
			cancelled = true;
		};
	}, [days, projectId]);

	const axis = useMemo(
		() => ({
			tick: { fill: theme.palette.text.secondary, fontSize: 12 },
			axisLine: { stroke: theme.palette.divider },
			tickLine: false,
		}),
		[theme],
	);
	const grid = <CartesianGrid vertical={false} stroke={theme.palette.divider} strokeDasharray="3 3" />;
	const series = theme.palette.primary.main;
	// ≥ 8px markers with a surface ring; also what shows a day that has no neighbour to draw a line to
	const dots = data && data.daily.length <= 31 ? { r: 4, strokeWidth: 2, fill: series, stroke: theme.palette.background.paper } : { r: 2, strokeWidth: 0, fill: series };

	const s = data?.summary;
	return (
		<Box>
			{/* Filters: one row above the charts */}
			<Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", mb: 3 }}>
				<ToggleButtonGroup size="small" exclusive value={days} onChange={(_, v) => v && setDays(v)} aria-label="Period">
					{PERIODS.map((p) => (
						<ToggleButton key={p} value={p}>
							Last {p} days
						</ToggleButton>
					))}
				</ToggleButtonGroup>
				<FormControl size="small" sx={{ minWidth: 220 }}>
					<InputLabel>Project</InputLabel>
					<Select label="Project" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
						<MenuItem value="">All projects</MenuItem>
						{(projects || []).map((p) => (
							<MenuItem key={p.id} value={p.id}>
								{p.name}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				{loading && data && <CircularProgress size={20} />}
			</Box>

			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}
			{loading && !data ? (
				<Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
					<CircularProgress />
				</Box>
			) : !data ? null : s.runs === 0 ? (
				<Alert severity="info">No finished runs in the last {data.days} days{projectId ? " for this project" : ""}.</Alert>
			) : (
				<>
					{data.truncated && (
						<Alert severity="warning" sx={{ mb: 2 }}>
							There are more runs than can be counted at once; these numbers cover the oldest 50,000. Pick a shorter period or a
							project.
						</Alert>
					)}
					<Grid container spacing={2} sx={{ mb: 3 }}>
						<Grid item xs={12} sm={6} md={3}>
							<StatTile label="Pass rate" value={`${s.passRate}%`}>
								<PassRateChange now={s.passRate} before={s.previousPassRate} />
							</StatTile>
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<StatTile label="Runs" value={s.runs.toLocaleString()}>
								<Typography variant="caption" color="text.secondary">
									{s.passed} passed · {s.failed} failed · {s.testsRun} tests
								</Typography>
							</StatTile>
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<StatTile label="Average run time" value={fmtDuration(s.avgDuration)}>
								<Typography variant="caption" color="text.secondary">
									Per test run
								</Typography>
							</StatTile>
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<StatTile label="Flaky tests" value={s.flakyTests}>
								<Typography variant="caption" color="text.secondary">
									Result changed 3+ times
								</Typography>
							</StatTile>
						</Grid>
					</Grid>

					<Grid container spacing={2} sx={{ mb: 1 }}>
						<Grid item xs={12} md={4}>
							<ChartCard title="Pass rate by day">
								<ResponsiveContainer>
									<LineChart data={data.daily} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
										{grid}
										<XAxis dataKey="date" tickFormatter={fmtDay} minTickGap={24} {...axis} />
										<YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} width={48} {...axis} />
										<ChartTooltip content={<DayTooltip />} cursor={{ stroke: theme.palette.divider }} />
										<Line type="monotone" dataKey="passRate" stroke={series} strokeWidth={2} dot={dots} activeDot={{ r: 5 }} connectNulls={false} isAnimationActive={false} />
									</LineChart>
								</ResponsiveContainer>
							</ChartCard>
						</Grid>
						<Grid item xs={12} md={4}>
							<ChartCard title="Runs by day">
								<ResponsiveContainer>
									<BarChart data={data.daily} margin={{ top: 8, right: 12, bottom: 0, left: 0 }} barCategoryGap={2}>
										{grid}
										<XAxis dataKey="date" tickFormatter={fmtDay} minTickGap={24} {...axis} />
										<YAxis allowDecimals={false} width={48} {...axis} />
										<ChartTooltip content={<DayTooltip />} cursor={{ fill: theme.palette.action.hover }} />
										<Bar dataKey="runs" fill={series} radius={[4, 4, 0, 0]} maxBarSize={24} isAnimationActive={false} />
									</BarChart>
								</ResponsiveContainer>
							</ChartCard>
						</Grid>
						<Grid item xs={12} md={4}>
							<ChartCard title="Average run time by day">
								<ResponsiveContainer>
									<LineChart data={data.daily} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
										{grid}
										<XAxis dataKey="date" tickFormatter={fmtDay} minTickGap={24} {...axis} />
										<YAxis tickFormatter={fmtAxisDuration} width={48} {...axis} />
										<ChartTooltip content={<DayTooltip />} cursor={{ stroke: theme.palette.divider }} />
										<Line type="monotone" dataKey="avgDuration" stroke={series} strokeWidth={2} dot={dots} activeDot={{ r: 5 }} connectNulls={false} isAnimationActive={false} />
									</LineChart>
								</ResponsiveContainer>
							</ChartCard>
						</Grid>
					</Grid>
					<Box sx={{ mb: 3 }}>
						<Button size="small" onClick={() => setShowTable((v) => !v)}>
							{showTable ? "Hide the numbers by day" : "Show the numbers by day"}
						</Button>
						{showTable && (
							<TableContainer component={Card} sx={{ mt: 1, maxHeight: 320 }}>
								<Table size="small" stickyHeader>
									<TableHead>
										<TableRow>
											<TableCell>Day</TableCell>
											<TableCell align="right">Runs</TableCell>
											<TableCell align="right">Passed</TableCell>
											<TableCell align="right">Failed</TableCell>
											<TableCell align="right">Pass rate</TableCell>
											<TableCell align="right">Average run time</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{[...data.daily].reverse().map((d) => (
											<TableRow key={d.date}>
												<TableCell>{fmtDay(d.date)}</TableCell>
												<TableCell align="right">{d.runs}</TableCell>
												<TableCell align="right">{d.passed}</TableCell>
												<TableCell align="right">{d.failed}</TableCell>
												<TableCell align="right">{d.passRate == null ? "–" : `${d.passRate}%`}</TableCell>
												<TableCell align="right">{fmtDuration(d.avgDuration)}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						)}
					</Box>

					<Grid container spacing={2}>
						<Grid item xs={12} lg={6}>
							<Card sx={{ height: "100%" }}>
								<CardContent>
									<Typography variant="subtitle1" fontWeight={600}>
										Failed most
									</Typography>
									{data.topFailing.length === 0 ? (
										<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
											No failures in this period.
										</Typography>
									) : (
										<Table size="small">
											<TableHead>
												<TableRow>
													<TableCell>Test</TableCell>
													<TableCell align="right">Failed</TableCell>
													<TableCell>Last error</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{data.topFailing.map((t) => (
													<TableRow key={t.testId}>
														<TableCell>
															<TestLink testId={t.testId} name={t.testName} />
														</TableCell>
														<TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
															{t.failures} of {t.runs} ({t.failRate}%)
														</TableCell>
														<TableCell sx={{ maxWidth: 260 }}>
															<Tooltip title={`Last failed ${formatDateTime(t.lastFailedAt)}${t.lastError ? `: ${t.lastError}` : ""}`}>
																<Typography variant="caption" color="text.secondary" noWrap component="div">
																	{t.lastError || "–"}
																</Typography>
															</Tooltip>
														</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									)}
								</CardContent>
							</Card>
						</Grid>
						<Grid item xs={12} lg={6}>
							<Card sx={{ height: "100%" }}>
								<CardContent>
									<Typography variant="subtitle1" fontWeight={600}>
										Flaky
									</Typography>
									<Typography variant="caption" color="text.secondary">
										Tests whose result changed 3 or more times in this period, with their last results (oldest first).
									</Typography>
									{data.flaky.length === 0 ? (
										<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
											No flaky tests in this period.
										</Typography>
									) : (
										<Table size="small">
											<TableHead>
												<TableRow>
													<TableCell>Test</TableCell>
													<TableCell>Recent results</TableCell>
													<TableCell align="right">Changes</TableCell>
													<TableCell align="right">Passed</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{data.flaky.map((t) => (
													<TableRow key={`${t.testId}#${t.dataRow}`}>
														<TableCell>
															<TestLink testId={t.testId} name={t.testName} />
															{t.dataRow != null && (
																<Typography variant="caption" color="text.secondary">
																	{" "}
																	(row {t.dataRow + 1})
																</Typography>
															)}
														</TableCell>
														<TableCell>
															<HistoryDots history={t.history} />
														</TableCell>
														<TableCell align="right">{t.flips}</TableCell>
														<TableCell align="right">
															{t.passRate}% of {t.runs}
														</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									)}
								</CardContent>
							</Card>
						</Grid>
						<Grid item xs={12} lg={6}>
							<Card sx={{ height: "100%" }}>
								<CardContent>
									<Typography variant="subtitle1" fontWeight={600}>
										Slowest
									</Typography>
									<Typography variant="caption" color="text.secondary">
										Average time of passing runs.
									</Typography>
									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell>Test</TableCell>
												<TableCell align="right">Average</TableCell>
												<TableCell align="right">Longest</TableCell>
												<TableCell align="right">Runs</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{data.slowest.map((t) => (
												<TableRow key={t.testId}>
													<TableCell>
														<TestLink testId={t.testId} name={t.testName} />
													</TableCell>
													<TableCell align="right">{fmtDuration(t.avgDuration)}</TableCell>
													<TableCell align="right">{fmtDuration(t.maxDuration)}</TableCell>
													<TableCell align="right">{t.runs}</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</CardContent>
							</Card>
						</Grid>
					</Grid>
				</>
			)}
		</Box>
	);
}
